import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import CaptionBox from 'components/common/CaptionBox';
import AbnormalCircle from 'components/common/AbnormalCircle';
import HBox from 'components/common/HBox';
import VBox from 'components/common/VBox';
import DoughnutChart from 'components/charts/DoughnutChart';
import AbnormalDietBoxChart from 'components/charts/AbnormalDietBoxChart';
import SummaryBox from '../ReportBloodGlocose/SummaryBox';
import Dot from 'components/common/Dot';
import React from 'react';
import useSetChartData from 'hooks/app/useSetChartData';
import useRect from 'hooks/common/useRect';

export default function DietRatio(props) {
  const {
    duration,
    mealData,
    ...rest
  } = props;
  const {
    postprandial_abnormal_rate,
    high_bld_sugr_cnt,
    low_bld_sugr_cnt,
    complex_bld_sugr_cnt,
    total_meal_cnt,
  } = mealData || {};

  const ref = React.useRef();
  const rect = useRect(ref);
  const setChartDoughnutData = useSetChartData('DietDistributionDoughnutChart');
  const setChartScatterData = useSetChartData('DietDistributionScatterChart');
  const chartDataTimeoutRef = React.useRef({});
  const onComplete = React.useCallback((type, setChartData) => (animation) => {
    if (mealData.uninitialized) return;
    if (duration) return;
    if (!animation.chart.canvas.width) return;
    if (!animation.chart.canvas.height) return;
    clearTimeout(chartDataTimeoutRef.current[type]);
    const url = animation.chart.canvas.toDataURL('image/png');
    const width = animation.chart.canvas.width;
    const height = animation.chart.canvas.height;
    chartDataTimeoutRef.current[type] = setTimeout(() => {
      setChartData && setChartData(() => ({ url, width, height }));
      URL.revokeObjectURL(url);
    }, 6000);
  }, [duration, mealData.uninitialized]);

  const doughnutChart = React.useMemo(() => (
    <DoughnutChart
      {...{
        caption: T('전체 {0}건', total_meal_cnt),
        value: [total_meal_cnt - high_bld_sugr_cnt - low_bld_sugr_cnt - complex_bld_sugr_cnt, high_bld_sugr_cnt, low_bld_sugr_cnt, complex_bld_sugr_cnt],
        onComplete: onComplete('setChartDoughnutData', setChartDoughnutData),
        duration,
      }}
    />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [complex_bld_sugr_cnt, duration, high_bld_sugr_cnt, low_bld_sugr_cnt, onComplete, setChartDoughnutData, total_meal_cnt, rect]);

  return (
    <HBox
      {...ex('DietRatio', rest)}
      {...{ ref }}
    >
      <VBox>
        <HBox {...ex('SummaryTitle')} between>
          <div>
            {T('식사 분포')}
          </div>
        </HBox>
        <HBox  {...ex('AbnormalDistribution')}>
          <VBox>
            <div>
              {T('식후 이상혈당 발생')}
            </div>
            <HBox {...ex('DietAbnormalCount')}>
              <div>
                {postprandial_abnormal_rate}
              </div>
              <div>
                {T('%')}
              </div>
            </HBox>
            <HBox {...ex('AbnormalSummary')}>
              <SummaryBox
                {...ex('Sub', {
                  caption: T('고혈당'),
                  value: high_bld_sugr_cnt,
                  unit: T('건'),
                })}
              >
                <Dot {...{ color: '#FF995A' }} />
              </SummaryBox>
              <SummaryBox
                {...ex('Sub', {
                  caption: T('저혈당'),
                  value: low_bld_sugr_cnt,
                  unit: T('건'),
                })}
              >
                <Dot {...{ color: '#756CE4' }} />
              </SummaryBox>
              <SummaryBox
                {...ex('Sub', {
                  caption: (
                    <VBox>
                      <div>
                        {T('변동혈당')}
                      </div>
                      <div>
                        {T('(고+저혈당)')}
                      </div>
                    </VBox>
                  ),
                  value: complex_bld_sugr_cnt,
                  unit: T('건'),
                })}
              >
                <Dot {...{ color: '#6ED5E1' }} />
              </SummaryBox>
            </HBox>
          </VBox>
          <div>
            {doughnutChart}
          </div>
        </HBox>
      </VBox>
      <AbnormalDietBoxChart {...props} {...{ onComplete: onComplete('setChartScatterData', setChartScatterData), duration }} />
    </HBox>
  );
}
